import { useState } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { trpc } from "@/lib/trpc";
import {
  DollarSign,
  TrendingUp,
  AlertTriangle,
  Download,
  Activity,
  BarChart3,
  Users,
} from "lucide-react";

function formatUsd(amount: number): string {
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

export default function AiCosts() {
  const [days, setDays] = useState(30);

  const costPerDay = trpc.admin.aiCosts.costPerDayPerCompany.useQuery({ days });
  const topConsumers = trpc.admin.aiCosts.topConsumers.useQuery({ days, limit: 20 });
  const alerts = trpc.admin.aiCosts.alerts.useQuery({ thresholdUsd: 100 });
  const exportQuery = trpc.admin.aiCosts.export.useQuery(
    { limit: 5000 },
    { enabled: false }
  );

  const hasError = costPerDay.isError || topConsumers.isError || alerts.isError;
  const errorMessage =
    costPerDay.error?.message ??
    topConsumers.error?.message ??
    alerts.error?.message ??
    "Erreur inconnue";

  const totalCost = costPerDay.data?.reduce((sum, r) => sum + r.totalCostUsd, 0) ?? 0;
  const totalCalls = costPerDay.data?.reduce((sum, r) => sum + r.totalCalls, 0) ?? 0;
  const avgCostPerDay = days > 0 ? totalCost / days : 0;

  const dailyTotals: Record<string, { cost: number; calls: number }> = {};
  for (const row of costPerDay.data ?? []) {
    if (!dailyTotals[row.day]) dailyTotals[row.day] = { cost: 0, calls: 0 };
    dailyTotals[row.day].cost += row.totalCostUsd;
    dailyTotals[row.day].calls += row.totalCalls;
  }
  const sortedDays = Object.entries(dailyTotals)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([day, data]) => ({ day, ...data }));
  const maxDailyCost = Math.max(...sortedDays.map((d) => d.cost), 1);

  const handleExportCsv = async () => {
    const result = await exportQuery.refetch();
    if (!result.data) return;

    const headers = [
      "date",
      "companyName",
      "companyId",
      "userId",
      "model",
      "context",
      "inputTokens",
      "outputTokens",
      "costUsd",
      "latencyMs",
    ];
    const csvRows = [headers.join(",")];
    for (const row of result.data) {
      csvRows.push(
        [
          row.date,
          `"${(row.companyName ?? "").replace(/"/g, '""')}"`,
          row.companyId,
          row.userId ?? "",
          row.model ?? "",
          `"${(row.context ?? "").replace(/"/g, '""')}"`,
          row.inputTokens ?? 0,
          row.outputTokens ?? 0,
          row.costUsd ?? 0,
          row.latencyMs ?? 0,
        ].join(",")
      );
    }

    const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ai-costs-export-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <AdminLayout>
      <div className="space-y-6 animate-fade-in">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight">Coûts IA</h1>
            <p className="text-muted-foreground">
              Suivi des coûts IA par entreprise — tokens, modèles, tendances
            </p>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
            >
              <option value={7}>7 jours</option>
              <option value={14}>14 jours</option>
              <option value={30}>30 jours</option>
              <option value={60}>60 jours</option>
              <option value={90}>90 jours</option>
            </select>
            <Button
              variant="outline"
              onClick={handleExportCsv}
              disabled={exportQuery.isFetching}
            >
              <Download className="h-4 w-4 mr-2" />
              {exportQuery.isFetching ? "Export…" : "Export CSV"}
            </Button>
          </div>
        </div>

        {/* Error state — évite le skeleton infini si l'API échoue */}
        {hasError && (
          <Card className="border-destructive/40 bg-destructive/5">
            <CardContent className="flex items-center gap-3 py-4">
              <AlertTriangle className="h-5 w-5 text-destructive shrink-0" />
              <div className="text-sm">
                <p className="font-medium text-destructive">
                  Impossible de charger les coûts IA
                </p>
                <p className="text-muted-foreground">{errorMessage}</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="ml-auto"
                onClick={() => {
                  costPerDay.refetch();
                  topConsumers.refetch();
                  alerts.refetch();
                }}
              >
                Réessayer
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Alerts */}
        {alerts.data && alerts.data.length > 0 && (
          <Card className="border-destructive/40 bg-destructive/5">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2 text-destructive">
                <AlertTriangle className="h-4 w-4" />
                Alertes — Entreprises &gt; $100/jour
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {alerts.data.map((a) => (
                  <div
                    key={a.companyId}
                    className="flex items-center justify-between text-sm p-2 rounded bg-background border"
                  >
                    <div>
                      <span className="font-medium">{a.companyName}</span>
                      <span className="text-muted-foreground ml-2">
                        ({a.callsToday} appels)
                      </span>
                    </div>
                    <div className="flex items-center gap-4">
                      <div>
                        <span className="text-destructive font-semibold">
                          {formatUsd(a.costToday)}
                        </span>
                        <span className="text-xs text-muted-foreground ml-1">
                          aujourd'hui
                        </span>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        moy. 30j : {formatUsd(a.avgDailyCost30d)}/j
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* KPI cards */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Coût total ({days}j)
              </CardTitle>
              <DollarSign className="h-4 w-4 text-primary" />
            </CardHeader>
            <CardContent>
              {costPerDay.isLoading ? (
                <Skeleton className="h-8 w-24" />
              ) : (
                <div className="text-2xl font-bold">{formatUsd(totalCost)}</div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Appels IA ({days}j)
              </CardTitle>
              <Activity className="h-4 w-4 text-primary" />
            </CardHeader>
            <CardContent>
              {costPerDay.isLoading ? (
                <Skeleton className="h-8 w-24" />
              ) : (
                <div className="text-2xl font-bold">
                  {totalCalls.toLocaleString("fr-FR")}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Coût moyen / jour
              </CardTitle>
              <TrendingUp className="h-4 w-4 text-success" />
            </CardHeader>
            <CardContent>
              {costPerDay.isLoading ? (
                <Skeleton className="h-8 w-24" />
              ) : (
                <div className="text-2xl font-bold">{formatUsd(avgCostPerDay)}</div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Alertes actives
              </CardTitle>
              <AlertTriangle className="h-4 w-4 text-warning" />
            </CardHeader>
            <CardContent>
              {alerts.isLoading ? (
                <Skeleton className="h-8 w-24" />
              ) : (
                <div className="text-2xl font-bold">
                  {alerts.data?.length ?? 0}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Daily cost chart */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              Coût / jour — {days} derniers jours
            </CardTitle>
          </CardHeader>
          <CardContent>
            {costPerDay.isLoading ? (
              <Skeleton className="h-48 w-full" />
            ) : sortedDays.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                Aucune donnée sur cette période
              </p>
            ) : (
              <div className="flex items-end gap-[2px] h-48">
                {sortedDays.map((d) => {
                  const pct = (d.cost / maxDailyCost) * 100;
                  const dayLabel = d.day.slice(5);
                  return (
                    <div
                      key={d.day}
                      className="flex-1 flex flex-col items-center gap-1 group min-w-0"
                    >
                      <div
                        className="w-full relative"
                        style={{ height: "160px" }}
                      >
                        <div
                          className="absolute bottom-0 w-full bg-primary rounded-t-sm opacity-70 group-hover:opacity-100 transition-opacity"
                          style={{ height: `${Math.max(pct, 1)}%` }}
                          title={`${d.day}: ${formatUsd(d.cost)} — ${d.calls} appels`}
                        />
                      </div>
                      <span className="text-[9px] text-muted-foreground truncate w-full text-center leading-tight">
                        {sortedDays.length > 14
                          ? sortedDays.indexOf(d) % 3 === 0
                            ? dayLabel
                            : ""
                          : dayLabel}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top consumers */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="h-4 w-4" />
              Top consommateurs — {days} derniers jours
            </CardTitle>
          </CardHeader>
          <CardContent>
            {topConsumers.isLoading ? (
              <Skeleton className="h-40 w-full" />
            ) : !topConsumers.data || topConsumers.data.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                Aucune donnée disponible
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-muted-foreground text-xs">
                      <th className="text-left pb-2 font-medium">#</th>
                      <th className="text-left pb-2 font-medium">Entreprise</th>
                      <th className="text-right pb-2 font-medium">Coût total</th>
                      <th className="text-right pb-2 font-medium">Appels</th>
                      <th className="text-right pb-2 font-medium">Coût moy./appel</th>
                      <th className="text-right pb-2 font-medium">Tokens IN</th>
                      <th className="text-right pb-2 font-medium">Tokens OUT</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topConsumers.data.map((row, i) => {
                      const isHighCost = row.totalCostUsd >= 100;
                      return (
                        <tr
                          key={row.companyId}
                          className="border-b last:border-0 hover:bg-muted/30"
                        >
                          <td className="py-2 text-muted-foreground">{i + 1}</td>
                          <td className="py-2 font-medium">
                            {row.companyName}
                            {isHighCost && (
                              <Badge variant="destructive" className="ml-2 text-[10px]">
                                &gt;$100
                              </Badge>
                            )}
                          </td>
                          <td className="py-2 text-right font-medium">
                            {formatUsd(row.totalCostUsd)}
                          </td>
                          <td className="py-2 text-right">
                            {row.totalCalls.toLocaleString("fr-FR")}
                          </td>
                          <td className="py-2 text-right text-muted-foreground">
                            {formatUsd(row.avgCostPerCall)}
                          </td>
                          <td className="py-2 text-right text-muted-foreground">
                            {row.totalInputTokens.toLocaleString("fr-FR")}
                          </td>
                          <td className="py-2 text-right text-muted-foreground">
                            {row.totalOutputTokens.toLocaleString("fr-FR")}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
